import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toTitleCase } from "../../../../utils/formatText";
import { AMC_AUDIENCE_STATUSES, CANNED_QUERIES, FEATURED_REPORTS } from "../../utils/utils";
import { resolveFilterOption } from "../../../merchandising/components/tables/resolveFilterOption";

const buildAudienceTemplateFilter = (templates = []) => {
  return {
    name: ($) => $.amc["Audience Type"],
    id: "template_display_name",
    format: "select",
    selectOptions: [
      {
        label: ($) => $.amc["All Types"],
        value: "all",
        clearable: false,
      },
      ...templates.map((audienceTemplate) => ({
        label: audienceTemplate.audience_display_name,
        value: audienceTemplate.audience_template_name,
      })),
    ],
    override: true,
    defaultValue: "all",
    operator: "=",
    filterFunction: (audience, filterValue) => {
      if (filterValue !== "all") {
        return audience.template_name === filterValue;
      }
      return true;
    },
  };
};

const buildAudienceTagsFilter = (templates = []) => {
  const allTemplateTags = [
    ...new Set(
      templates?.flatMap((t) => {
        return t.recommended_tags.length ? JSON.parse(t.recommended_tags) : [];
      }) ?? [],
    ),
  ].sort();

  return {
    name: ($) => $.amc["Tags"],
    id: "audience_template_tags",
    format: "multi-select",
    selectOptions: [
      {
        label: ($) => $.amc["All Types"],
        value: "all",
        clearable: false,
      },
      ...allTemplateTags.map((tag) => ({
        label: tag?.toLowerCase() === "fsi" ? "FSI" : toTitleCase(tag),
        value: tag,
      })),
    ],
    override: true,
    defaultValue: "all",
    operator: "=",
    filterFunction: (audience, filterValue) => {
      if (filterValue !== "all") {
        const parsedAudienceTags = audience.recommended_tags.length ? JSON.parse(audience.recommended_tags) : [];

        return parsedAudienceTags.includes(filterValue);
      }
      return true;
    },
  };
};

export const AUDIENCE_STATUS_FILTER = {
  name: ($) => $.amc["Status"],
  id: "status",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.amc["All Statuses"],
      value: "all",
      clearable: false,
    },
    ...AMC_AUDIENCE_STATUSES,
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (audience, filterValue) => {
    if (filterValue !== "all") {
      return audience.status === filterValue;
    }
    return true;
  },
};

const buildAudienceAsinsFilter = (asins) => {
  const asinOptions = [...new Set(asins)]?.map((asin) => ({
    value: asin,
    label: asin,
  }));

  return {
    name: ($) => $.amc["ASINs"],
    id: "audience_asins",
    format: "select",
    selectOptions: [
      {
        value: "all",
        label: ($) => $.amc["All ASINs"],
      },
      ...asinOptions.flat(),
    ],
    override: true,
    defaultValue: "all",
    operator: "custom",
    filterFunction: (audience, filterValue) => {
      if (filterValue === "all") {
        return true;
      }

      const params = audience.parameters ?? {};
      const audienceAsins = params.asins ?? params.tracked_item_asins ?? [];

      return audienceAsins.includes(filterValue);
    },
  };
};

export const AMC_REPORT_TYPE_FILTER = {
  name: ($) => $.amc["Report Type"],
  id: "name",
  format: "select",
  selectOptions: [
    {
      label: ($) => $.amc["All Report Types"],
      value: "all",
      clearable: false,
    },
    ...[...CANNED_QUERIES, ...FEATURED_REPORTS].map((type) => {
      return { label: type.display_name, value: type.name };
    }),
  ],
  override: true,
  defaultValue: "all",
  operator: "=",
  filterFunction: (subscription, filterValue) => {
    if (filterValue !== "all") {
      return subscription.name === filterValue;
    }
    return true;
  },
};

// Resolves the amc-namespace selectors carried by the filter defs above into
// plain strings, bound to the `amc` namespace at the call site so the generic
// FilterWidget never has to resolve them. See resolveFilterOption.
export function useAmcLifecycleFilters() {
  const { t } = useTranslation(["amc"]);
  return useMemo(
    () => ({
      AUDIENCE_STATUS_FILTER: resolveFilterOption(t, AUDIENCE_STATUS_FILTER),
      AMC_REPORT_TYPE_FILTER: resolveFilterOption(t, AMC_REPORT_TYPE_FILTER),
      buildAudienceTemplateFilter: (templates) => resolveFilterOption(t, buildAudienceTemplateFilter(templates)),
      buildAudienceTagsFilter: (templates) => resolveFilterOption(t, buildAudienceTagsFilter(templates)),
      buildAudienceAsinsFilter: (asins) => resolveFilterOption(t, buildAudienceAsinsFilter(asins)),
    }),
    [t],
  );
}
